/**
 * JSON Field Utilities
 * Provides safe parsing, validation, and stringification for JSON fields
 */

import type {
  JsonObject,
  JsonKeyValuePair,
} from "@/components/shared/types/json-field-types";

/** Runtime guard for "a JSON object" (not an array, not null). */
function isJsonObject(value: unknown): value is JsonObject {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * Check if a value is valid JSON
 */
export function isValidJSON(value: unknown): boolean {
  if (value === null || value === undefined) {
    return false;
  }

  if (typeof value === "object") {
    return true;
  }

  if (typeof value === "string") {
    try {
      JSON.parse(value);
      return true;
    } catch {
      return false;
    }
  }

  return false;
}

/**
 * Safely parse JSON value, returning null if invalid
 */
export function parseJSONSafely(value: unknown): JsonObject | null {
  if (value === null || value === undefined) {
    return null;
  }

  // Already an object
  if (isJsonObject(value)) {
    return value;
  }

  // Try to parse string
  if (typeof value === "string") {
    try {
      const parsed: unknown = JSON.parse(value);
      if (isJsonObject(parsed)) {
        return parsed;
      }
    } catch {
      return null;
    }
  }

  return null;
}

/**
 * Stringify JSON object with formatting
 */
export function stringifyJSON(obj: JsonObject): string {
  try {
    return JSON.stringify(obj, null, 2);
  } catch {
    return "{}";
  }
}

/**
 * Validate a key-value pair
 */
export function validateKeyValuePair(
  key: string,
  value: unknown
): { valid: boolean; error?: string } {
  // Key validation
  if (!key || typeof key !== "string") {
    return { valid: false, error: "Key must be a non-empty string" };
  }

  if (key.trim().length === 0) {
    return { valid: false, error: "Key cannot be empty or whitespace" };
  }

  // Value validation - must be JSON serializable
  try {
    JSON.stringify(value);
    return { valid: true };
  } catch {
    return { valid: false, error: "Value must be JSON serializable" };
  }
}

/**
 * Get error message from JSON parsing attempt
 */
export function getJSONParseError(value: string): string | null {
  try {
    JSON.parse(value);
    return null;
  } catch (error) {
    if (error instanceof SyntaxError) {
      return error.message;
    }
    return "Invalid JSON format";
  }
}

/**
 * Convert object to key-value pairs array
 */
export function objectToKeyValuePairs(obj: JsonObject): JsonKeyValuePair[] {
  return Object.entries(obj).map(([key, value]) => ({
    key,
    value,
  }));
}

/**
 * Convert key-value pairs array back to object
 */
export function keyValuePairsToObject(pairs: JsonKeyValuePair[]): JsonObject {
  return pairs.reduce<JsonObject>((acc, { key, value }) => {
    acc[key] = value;
    return acc;
  }, {});
}

/**
 * Format value for display (truncate long strings)
 */
export function formatValueForDisplay(value: unknown, maxLength: number = 50): string {
  if (value === null || value === undefined) {
    return "null";
  }

  if (typeof value === "string") {
    return value.length > maxLength ? value.substring(0, maxLength) + "..." : value;
  }

  if (typeof value === "boolean") {
    return value ? "true" : "false";
  }

  if (typeof value === "number") {
    return String(value);
  }

  if (typeof value === "object") {
    const str = JSON.stringify(value);
    return str.length > maxLength ? str.substring(0, maxLength) + "..." : str;
  }

  return String(value);
}
